// Analogous to Tfs/Service/WebAccess/Widgets/Scripts/DataServices/ConfigurationQueries/AggregationWorkItemTypeFieldsQuery.ts
/// <amd-module name="Analytics/Boards/BoardsQueries/AggregationFieldsQuery" />
import { AnalyticsCacheableQueryBase, CommonFeatureCommandName } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";

export interface WorkItemTypeField {
    FieldName: string;
    FieldReferenceName: string;
    FieldType: string;
    WorkItemType: string;
}

/** Produces an unfiltered project-scoped inventory of work item-fields for configuration experiences. */
export class AggregationFieldsQuery extends AnalyticsCacheableQueryBase<WorkItemTypeField[]> {
    private static generateQueryOptions(projectId: string): ODataQueryOptions {
        const $filter = "(FieldType eq 'Integer' or FieldType eq 'Double') and (not startswith(FieldReferenceName,'System.'))";
        return {
            entityType: "WorkItemTypeFields",
            oDataVersion: latestODataVersion,
            project: projectId,
            $filter: $filter
        };
    }

    public constructor(projectId: string) {
        super(CommonFeatureCommandName, AggregationFieldsQuery.generateQueryOptions(projectId));
    }

    public getQueryName(): string {
        return "AggregationWorkItemTypeFieldsQuery";
    }
}

export interface TypeField {
    FieldName: string;
    FieldReferenceName: string;
    FieldType: string;
}

// Note, this was a policy step formerly covered by Velocity config.
// This step could be manifested as a second order cacheable query call, but it will require a breaking change (to supply the query cache as a callable resource), which will need buy-in to move a ahead.
export function getDistinctFieldsOfTypes(workItemTypes: string[], fields: WorkItemTypeField[]): TypeField[] {
    // Get Fields associated with specified types
    const fieldsOfType = fields.filter((field: WorkItemTypeField) => {
        return workItemTypes.some((workItemType: string) => {
            return field.WorkItemType === workItemType && !isRecognizedNonAggregationField(field.FieldReferenceName);
        });
    });

    // Get unique instances of fields and omit type information.
    let knownFields: { [key: string]: number } = {};
    let distinctTypes: TypeField[] = [];
    for (let item of fieldsOfType) {
        if (typeof knownFields[item.FieldReferenceName] == "undefined") {
            distinctTypes.push({
                FieldName: item.FieldName,
                FieldReferenceName: item.FieldReferenceName,
                FieldType: item.FieldType
            });
        }
        // Mark as known
        knownFields[item.FieldReferenceName] = 0;
    }
    return distinctTypes;
}

/** Describes known numeric fields which have no value for aggregation. This allows us to filter them out of the experience.  */
export function isRecognizedNonAggregationField(fieldReferenceName: string) {
    const knownNumericNonAggregationFields = [
        "Microsoft.VSTS.Common.StackRank",
        "Microsoft.VSTS.Common.Priority",
        "Microsoft.VSTS.Common.BacklogPriority",
        "Microsoft.VSTS.Common.TimeCriticality"
    ];
    return (
        knownNumericNonAggregationFields.findIndex(candidateName => {
            return candidateName === fieldReferenceName;
        }) >= 0
    );
}
